import { BillingProduct } from "./constants";

export type UserState =
  | "onboarding"
  | "free_exploration"
  | "trial_active"
  | "subscribed"
  | "trial_expired";

export interface DeriveUserStateInput {
  onboardingCompleted: boolean;
  plan: BillingProduct;
  isCanceled: boolean;
  stripeStatus: "active" | "trialing" | null;
  hasPastSubscription: boolean;
}

/**
 * Pure derivation of user lifecycle state from DB-sourced inputs.
 *
 * Priority order:
 *   1. Active subscription (trialing or active) — overrides onboarding flag
 *   2. Onboarding incomplete — "onboarding"
 *   3. FREE plan with past subscription — "trial_expired"
 *   4. FREE plan without past subscription — "free_exploration"
 *   5. Fallback — "subscribed"
 */
export function deriveUserState(input: DeriveUserStateInput): UserState {
  const {
    onboardingCompleted,
    plan,
    isCanceled,
    stripeStatus,
    hasPastSubscription,
  } = input;

  if (plan !== BillingProduct.FREE && !isCanceled) {
    return stripeStatus === "trialing" ? "trial_active" : "subscribed";
  }

  if (!onboardingCompleted) {
    return "onboarding";
  }

  if (plan === BillingProduct.FREE) {
    return hasPastSubscription ? "trial_expired" : "free_exploration";
  }

  return "subscribed";
}

/**
 * Pure derivation: can the user perform gated actions given their lifecycle
 * state?
 *
 * FREE users (free_exploration) can attempt actions — feature-level quotas
 * and access (e.g. 1 scan/month) are enforced server-side. The trial dialog
 * gates only trial_expired and onboarding states.
 */
export function canPerformActionFor(userState: UserState): boolean {
  return (
    userState === "trial_active" ||
    userState === "subscribed" ||
    userState === "free_exploration"
  );
}
